import { useEffect, useRef, useState } from 'react'
import GenericFormHeaderComponent from '../../../genericComponents/forms/GenericFormHeaderComponent';
import GenericInput from '../../../genericComponents/input/GenericInputComponent';
import useCustomToast from '../../../hooks/useCustomToast';
import { formatFileSize, getFileNameFromExt } from '../../../helper/helperTableFunctions';
import GenericCustomButton from '../../../genericComponents/GenericCustomButton';
import Icons from '../../../assets/icons';
import FullPageLoader from '../../../loaders/FullPageLoader';
import { CONFIG } from '../../../config';
import axios from 'axios';

const ReportUploadModal = (props) => {

    const { closeModal, data } = props;
    const [dropDown1, setDropDown1] = useState(data?.details?.dropdown1Status?.toUpperCase() !== "PENDING" ? data?.details?.dropdown1Status?.toUpperCase() : null);
    const [dropDown2, setDropDown2] = useState(data?.details?.dropdown2Status?.toUpperCase() !== "PENDING" ? data?.details?.dropdown2Status?.toUpperCase() : null);
    const [file1, setFile1] = useState(null)
    const [file2, setFile2] = useState(null)
    const [text, setText] = useState({
        text1: "",
        text2: ""
    })
    const [checkBox, setCheckBox] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [loading, setLoading] = useState(false);

    const file1Ref = useRef();
    const file2Ref = useRef()

    const isHold1 = dropDown1?.toUpperCase() === "HOLD" 
    const isHold2 = dropDown2?.toUpperCase() === "HOLD"

    const customToast = useCustomToast();

    const handleFileChange = (e, type) => {
        let file = e?.target?.files?.[0];
        let fileName = file?.name;
        let extension = fileName?.split(".").pop()?.toLowerCase();

        const supportedExtensions = ["pdf", "xlsx", "csv", "jpeg", "png", "jpg"];

        if (supportedExtensions.includes(extension)) {
            if (file.size / (1024 * 1024) > 10) {
                customToast({
                    title: "File Size Error!",
                    description: "File size exceeds 10MB limit.",
                    status: "error",
                });
                return;
            }
            if (type === 'fpt') {
                setFile1(file);
                if (file1Ref.current) file1Ref.current.value = null;
            } else {
                setFile2(file);
                if (file2Ref.current) file2Ref.current.value = null;
            }
        } else {
            customToast({
                title: "File Validation Error!",
                description: `Invalid file selected! Only PDF, XLSX, CSV, JPEG, PNG are allowed.`,
                status: "error",
            });
            if (type === 'fpt') {
                if (file1Ref.current) file1Ref.current.value = null;
            } else {
                if (file2Ref.current) file2Ref.current.value = null;
            }
        }
    }

    useEffect(() => {
        if ((data?.details?.dropdown2Status?.toUpperCase() === "PASSED" || data?.details?.dropdown2Status?.toUpperCase() === "FAILED" || data?.details?.dropdown2Status?.toUpperCase() === "NA") && (data?.details?.dropdown1Status?.toUpperCase() === "PASSED" || data?.details?.dropdown1Status?.toUpperCase() === "FAILED" || data?.details?.dropdown1Status?.toUpperCase() === "NA")) {
            setButtonDisabled(true)
        } else {
            setButtonDisabled(false)
        }
    }, [data?.details?.dropdown1Status, data?.details?.dropdown2Status])

    const clearData = () => {
        setDropDown1(null);
        setDropDown2(null);
        setFile1(null);
        setFile2(null);
        setText({
            text1: "",
            text2: ""
        })
    }

    const UploadToS3 = async (file1, file2) => {
        try {
            setLoading(true);
            const formData = new FormData();
            if (file1) formData.append("file", file1);
            if (file2) formData.append("file", file2);
            formData.append("documentId", 0)
            formData.append('requestId', '');
            formData.append('type', 'TNA');
            const response = await axios.post(`${CONFIG.BASE_URL}/utility/document/upload`, formData, {
                headers: {
                    "X-Auth-Token": sessionStorage.getItem("emailToken"),
                    "X-Scope-Id": "common.commonfunctionality.upload"
                }
            });
            const resources = response?.data?.data?.resource || [];
            const normalize = (name) => name?.replace(/ /g, '_').replace(/\//g, '-');
            return {
                file1URL: file1 ? resources.find(r => normalize(r.oldFileName) === normalize(file1.name))?.fileKey ?? null : null,
                file2URL: file2 ? resources.find(r => normalize(r.oldFileName) === normalize(file2.name))?.fileKey ?? null : null,
            };
        } catch (e) {
            customToast({
                title: "Error!",
                description: "Something went wrong.",
                status: "error",
            });
            setLoading(false);
            return { file1URL: null, file2URL: null };
        }
    }

    const submit = async () => {
        try {
            if ((!file1 && !isHold1 && !(["FAILED", "PASSED", "NA"].includes(data?.details?.dropdown1Status?.toUpperCase())) && dropDown1!==null) || (!file2 && !isHold2 && !(["FAILED", "PASSED", "NA"].includes(data?.details?.dropdown2Status?.toUpperCase())) && dropDown2!==null)) {
                customToast({
                    title: "File Error!",
                    description: "Please select report to proceed.",
                    status: "error",
                });
                return;
            }
            if (
                (dropDown1 !== "HOLD" && (!!dropDown1 !== !!file1)) && !(["FAILED", "PASSED", "NA"].includes(data?.details?.dropdown1Status?.toUpperCase())) ||
                (dropDown2 !== "HOLD" && (!!dropDown2 !== !!file2)) && !(["FAILED", "PASSED", "NA"].includes(data?.details?.dropdown2Status?.toUpperCase()))
            ) {
                customToast({
                    title: "File Error!",
                    description: "Please select the matching file and status.",
                    status: "error",
                });
                return;
            }
            if (data?.checkbox && !checkBox && !isHold1 && !isHold2) {
                customToast({
                    title: "Checkbox Error!",
                    description: "Please accept the checkbox to proceed.",
                    status: "error",
                });
                return;
            }
            let file1URL = null, file2URL = null;
            if (file1 || file2) {
                const result = await UploadToS3(file1, file2);
                file1URL = result?.file1URL ?? null;
                file2URL = result?.file2URL ?? null;
            }
            
            if (data?.submission?.API === "purchase/order/lab/report/submit") {
                const body = {
                    trfCode: data?.details?.code,
                    fptStatus: dropDown1?.toUpperCase() ?? data?.details?.dropdown1Status,
                    gptStatus: dropDown2?.toUpperCase() ?? data?.details?.dropdown2Status,
                    fptDocument: file1URL ?? null,
                    gptDocument: file2URL ?? null,
                    fptRemarks: text?.text1 ?? data?.details?.remarks1 ?? "",
                    gptRemarks: text?.text2 ?? data?.details?.remarks2 ?? "",
                }
                const response = await axios.post(`${data?.submission?.BACKEND_BASE_URL}/${data?.submission?.API}`, body, {
                    headers: {
                        "X-Auth-Token": data?.submission?.token,
                        "X-Scope-Id": data?.submission?.scopeId
                    }
                })
                if (response?.data?.success) {
                    customToast({
                        title: "Success!",
                        description: "Report uploaded successfully.",
                        status: "success",
                    });
                }
            }
            closeModal();
        } catch (e) {
            customToast({
                title: "Error!",
                description: "Something went wrong.",
                status: "error",
            });
        } finally {
            setLoading(false);
        }
    }


    const renderUploadArea = (type, file, setFile, slot) => {
        const isCurrentSlotOnHold = slot === "slot1" ? isHold1 : isHold2;
        console.log("isCurrentSlotOnHold", isCurrentSlotOnHold)
        return (
            <div className='flex-1 flex flex-col h-full'>
                <label className='text-xs font-medium text-[#6A6C70] mb-1 block'>{data?.upload?.label || "Attach Report"}</label>
                {!file ? (
                    <GenericInput
                        className='!block'
                        genInputExtraClass='h-full'
                        gibInnerClassName='!h-full'
                        type="upload"
                        inputKey={`uploadFile-${type}`}
                        onChange={(e) => handleFileChange(e, type)}
                        value={null}
                        disabled={slot === "slot1"
                            ? isHold1 || ["PASSED","FAILED","NA"].includes(data?.details?.dropdown1Status?.toUpperCase())
                            : isHold2 || ["PASSED","FAILED","NA"].includes(data?.details?.dropdown2Status?.toUpperCase())}
                        inputProps={() => ({
                            maxSize: 10,
                            acceptFiles: data?.upload?.supportedFileTypes?.map(ext => "." + ext.toLowerCase()).join(",") || ".pdf,.xlsx,.csv,.jpeg,.png,.jpg",
                            renderCustomPlaceholder: () => {
                                return (
                                    <div className='gibu-inner mx-auto text-center flex flex-col items-center justify-center h-full p-4 min-h-[150px]'>
                                        <div className='inline-block mx-auto mb-3'>
                                            {["PASSED", "FAILED", "NA"].includes(
                                                data?.details?.[`dropdown${slot === "slot1" ? 1 : 2}Status`]?.toUpperCase()
                                            ) || isCurrentSlotOnHold ? <div className='bg-red-500 p-4 rounded-full ' ><Icons.CrossIcon height={40} width={40} className='text-white' /></div> : <Icons.UploadIconBlue height={50} width={50} />}
                                        </div>
                                        <div className='text-sm font-semibold text-[#111D31] mb-1'>
                                            {["PASSED", "FAILED"].includes(
                                                data?.details?.[`dropdown${slot === "slot1" ? 1 : 2}Status`]?.toUpperCase()
                                            ) ? <span>You already submit the <span className='text-red-500'>Report</span></span> :
                                            isCurrentSlotOnHold ? <span>You hold the <span className='text-red-500'>Report</span></span> :
                                                ["NA"].includes(
                                                    data?.details?.[`dropdown${slot === "slot1" ? 1 : 2}Status`]?.toUpperCase()
                                                ) ? <span>Not allowed to upload <span className='text-red-500'>Report</span></span> : <span>Drag & Drop an document file, or <span className='text-[#3B82F6]'>Browse</span></span>}
                                        </div>
                                        <div className='text-[11px] font-medium text-[#718096]'>
                                            {["PASSED", "FAILED", "NA"].includes(
                                                data?.details?.[`dropdown${slot === "slot1" ? 1 : 2}Status`]?.toUpperCase()
                                            ) || isCurrentSlotOnHold ? "" : `Max file size : ${data?.upload?.maxSizeSupported || "10MB"} | Supported: ${data?.upload?.supportedFileTypes?.join(", ") || "PDF, XLSX, CSV, JPEG, PNG"}`}
                                        </div>
                                    </div>
                                )
                            }
                        })}
                    />
                ) : (
                    <div className='h-full min-h-[150px] border border-dashed border-[#D6D9E4] rounded-[5px] bg-[#F8FAFC] flex flex-col items-center justify-center p-4 relative w-full'>
                        <div className='flex flex-col items-center gap-2 w-full'>
                            <div className='p-2 bg-white border border-[#E2E8F0] rounded'>
                                {(() => {
                                    const ext = file.name.split('.').pop().toLowerCase();
                                    const type = getFileNameFromExt(ext);
                                    switch (type) {
                                        case 'image': return <Icons.ImageIcon height={50} width={50} />;
                                        case 'excel': return <Icons.ExcelFileIcon height={50} width={50} />;
                                        case 'doc': return <Icons.WordFileIcon height={50} width={50} />;
                                        case 'video': return <Icons.VideoIcon height={50} width={50} />;
                                        default: return <Icons.PdfFileIcon height={50} width={50} />;
                                    }
                                })()}
                            </div>
                            <div className='text-center w-full'>
                                <p className='text-sm font-semibold text-[#111D31] mb-0 truncate w-full'>{file.name}</p>
                                <p className='text-xs text-[#64748B] mb-0'>{formatFileSize(file.size)}</p>
                            </div>
                        </div>
                        <div className='absolute top-2 right-2'>
                            <GenericCustomButton
                                leftIcon={<Icons.CrossIcon />}
                                onClick={() => setFile(null)}
                                type="default"
                                className='!p-1 hover:bg-gray-200 rounded-full'
                            />
                        </div>
                    </div>
                )}
            </div>
        )
    }


    return (
        <>
            <div className='h-full flex flex-col bg-white'>
                <GenericFormHeaderComponent
                    label={{
                        contentPrimary: (
                            <div className="flex items-center gap-2">
                                <span>{data?.heading || `Submit Report(FPT & GPT)`}</span>
                                {<span className="bg-[#F4F4F4] px-2 py-1 rounded text-xs font-medium text-[#6A6C70]">{data?.details?.code}</span>}
                            </div>
                        )
                    }}
                    closeModal={closeModal}
                />

                <div className='p-5 overflow-y-auto flex-1 grid grid-cols-2 gap-5'>
                    <div className='flex flex-col gap-4 col-span-1 pl-4'>
                        <div>
                            <GenericInput
                                label={data?.dropdown1?.label || "FPT Status"}
                                type="dropdown"
                                placeholder="Select"
                                mandatory={data?.dropdown1?.required}
                                value={["PASSED", "FAILED"].includes(data?.details?.dropdown1Status?.toUpperCase()) ? data?.details?.dropdown1Status : dropDown1 ?? (data?.details?.dropdown1Status?.toUpperCase() === "HOLD" ? data?.details?.dropdown1Status : null)}
                                isDisabled={data?.details?.dropdown1Status?.toUpperCase() === "PASSED" || data?.details?.dropdown1Status?.toUpperCase() === "FAILED" || data?.details?.dropdown1Status?.toUpperCase() === "NA"}
                                onChange={(val) => {
                                    setDropDown1(val?.value?.toUpperCase() || val?.toUpperCase())
                                }}
                                iconRight={<Icons.DownArrowNew />}
                                defaultValue= {data?.details?.dropdown1Status?.toUpperCase() === "HOLD" ? "hold" : ""}
                                inputProps={() => ({
                                    modalMainClass: '!top-full !bottom-auto [.completeProductSurvey_&_.modal-data-list-render]:!p-0',
                                    headerKeys: {
                                        "value": {}
                                    },
                                    itemKeys: {
                                        "value": ["value"]
                                    },
                                    getListData: () => {
                                        return data?.dropdown1?.options?.map(item => ({
                                            key: item.value,
                                            value: item.value?.toUpperCase()
                                        })) || [];
                                    },
                                })}
                            />

                            <GenericInput
                                label={data?.textArea1?.label || "FPT Remark"}
                                key="text1"
                                type="textarea"
                                className="!max-h-[100px] !overflow-y-auto"
                                value={data?.details?.text1 || text?.text1 || ""}
                                onChange={(val) => setText({ ...text, text1: val?.target?.value || "" })}
                                isDisabled={data?.details?.dropdown1Status?.toUpperCase() === "PASSED" || data?.details?.dropdown1Status?.toUpperCase() === "FAILED" || data?.details?.dropdown1Status?.toUpperCase() === "NA"}
                            />
                        </div>
                        {renderUploadArea('fpt', file1, setFile1, "slot1")}
                    </div>
                    <div className='flex flex-col gap-4 col-span-1 pr-4'>
                        <div>
                            <GenericInput
                                label={data?.dropdown2?.label || "GPT Status"}
                                type="dropdown"
                                placeholder="Select"
                                value={["PASSED", "FAILED"].includes(data?.details?.dropdown2Status?.toUpperCase()) ? data?.details?.dropdown2Status : dropDown2 ?? (data?.details?.dropdown2Status?.toUpperCase() === "HOLD" ? data?.details?.dropdown2Status : null)}
                                isDisabled={data?.details?.dropdown2Status?.toUpperCase() === "PASSED" || data?.details?.dropdown2Status?.toUpperCase() === "FAILED" || data?.details?.dropdown2Status?.toUpperCase() === "NA"}
                                mandatory={data?.dropdown2?.required}
                                onChange={(val) => setDropDown2(val?.value?.toUpperCase() || val?.toUpperCase())}
                                iconRight={<Icons.DownArrowNew />}
                                inputProps={() => ({
                                    modalMainClass: '!top-full !bottom-auto [.completeProductSurvey_&_.modal-data-list-render]:!p-0',
                                    headerKeys: {
                                        "value": {}
                                    },
                                    itemKeys: {
                                        "value": ["value"]
                                    },
                                    getListData: () => {
                                        return data?.dropdown2?.options?.map(item => ({
                                            key: item.value,
                                            value: item.value?.toUpperCase()
                                        })) || [];
                                    },
                                })}

                            />
                            <GenericInput
                                label={data?.textArea2?.label || "GPT Remark"}
                                key="text2"
                                type="textarea"
                                className="!max-h-[100px] !overflow-y-auto"
                                value={data?.details?.text2 || text?.text2 || ""}
                                onChange={(val) => setText({ ...text, text2: val?.target?.value || "" })}
                                isDisabled={data?.details?.dropdown2Status?.toUpperCase() === "PASSED" || data?.details?.dropdown2Status?.toUpperCase() === "FAILED" || data?.details?.dropdown2Status?.toUpperCase() === "NA"}
                            />
                        </div>
                        {renderUploadArea('gpt', file2, setFile2, "slot2")}
                    </div>
                </div>

                {data?.checkbox && <div className='ml-3 mb-3'>
                    <label className={`generic-checkbox ${buttonDisabled ? 'opacity-50 !cursor-not-allowed' : ''}`}>
                        <input
                            type="checkbox"
                            checked={checkBox}
                            onChange={(e) => {
                                return setCheckBox(e.target.checked)
                            }}
                            disabled={buttonDisabled}
                            className="!cursor-not-allowed"
                        />

                        <span className={`checkmark1 ${buttonDisabled ? 'opacity-50 !cursor-not-allowed' : ''}`}></span>

                        <span className={`text-[#67768e] text-xs font-medium ${buttonDisabled ? 'opacity-50 !cursor-not-allowed' : ''}`}>
                            {data?.checkbox?.required && <span className="mandatory">*</span>} {data?.checkbox?.label}
                        </span>
                    </label>
                </div>}
                <div className='flex gap-3 justify-around items-center mx-3 mb-3'>
                    <GenericCustomButton
                        label="Clear Data"
                        type="default"
                        onClick={clearData}
                        disabled={buttonDisabled}
                        className="bg-gray-100 text-gray-700 border-none hover:bg-gray-200 flex-1"
                    />
                    <GenericCustomButton
                        label="Submit TRF"
                        type="primary"
                        onClick={submit}
                        className="text-white flex-1"
                        disabled={buttonDisabled}
                    />
                </div>
            </div>
            {loading && <FullPageLoader style={{ opacity: 0.5 }} />}
        </>
    )
}

export default ReportUploadModal
